import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Asset } from '../types';
import { Activity, AlertTriangle, CheckCircle } from 'lucide-react';
import { useHistorian } from '../context/HistorianContext';

interface ProcessFlowDiagramProps {
  assets: Asset[];
}

export const ProcessFlowDiagram: React.FC<ProcessFlowDiagramProps> = ({ assets }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const { getTagValue } = useHistorian();

  useEffect(() => {
    if (!svgRef.current || assets.length < 2) return;

    const svg = d3.select(svgRef.current);
    const width = 1200;
    const height = 600;

    // Clear previous content
    svg.selectAll("*").remove();

    // Set up the SVG
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Create definitions for filters and gradients
    const defs = svg.append("defs");

    // Create metallic effect for assets
    const metallicGradient = defs.append("linearGradient")
      .attr("id", "metallic-gradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    metallicGradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#f8fafc")
      .attr("stop-opacity", 0.8);

    metallicGradient.append("stop")
      .attr("offset", "50%")
      .attr("stop-color", "#e2e8f0")
      .attr("stop-opacity", 0.9);

    metallicGradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#cbd5e1")
      .attr("stop-opacity", 0.8);

    // Create water flow gradient
    const waterGradient = defs.append("linearGradient")
      .attr("id", "water-gradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "0%");

    waterGradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#60a5fa")
      .attr("stop-opacity", 0.3);

    waterGradient.append("stop")
      .attr("offset", "50%")
      .attr("stop-color", "#3b82f6")
      .attr("stop-opacity", 0.5);

    waterGradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#60a5fa")
      .attr("stop-opacity", 0.3);

    // Create water pattern
    const waterPattern = defs.append("pattern")
      .attr("id", "water-pattern")
      .attr("width", "20")
      .attr("height", "20")
      .attr("patternUnits", "userSpaceOnUse")
      .attr("patternTransform", "rotate(45)");

    waterPattern.append("path")
      .attr("d", "M-1,1 l2,-2 M0,20 l20,-20 M19,21 l2,-2")
      .attr("stroke", "#93c5fd")
      .attr("stroke-width", "1")
      .attr("stroke-opacity", "0.3");

    // Create 3D effect filters
    const dropShadow = defs.append("filter")
      .attr("id", "drop-shadow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    dropShadow.append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", "3");

    dropShadow.append("feOffset")
      .attr("dx", "2")
      .attr("dy", "4");

    dropShadow.append("feComponentTransfer")
      .append("feFuncA")
      .attr("type", "linear")
      .attr("slope", "0.3");

    const merge = dropShadow.append("feMerge");
    merge.append("feMergeNode");
    merge.append("feMergeNode").attr("in", "SourceGraphic");

    // Create inner shadow for depth
    const innerShadow = defs.append("filter")
      .attr("id", "inner-shadow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    innerShadow.append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", "2")
      .attr("result", "blur");

    innerShadow.append("feOffset")
      .attr("in", "blur")
      .attr("dx", "2")
      .attr("dy", "2")
      .attr("result", "offsetBlur");

    const innerShadowMerge = innerShadow.append("feMerge");
    innerShadowMerge.append("feMergeNode").attr("in", "offsetBlur");
    innerShadowMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Create main group
    const g = svg.append("g");

    // Calculate node positions in a cascading layout
    const nodeWidth = 180;
    const nodeHeight = 100;
    const horizontalGap = (width - nodeWidth) / (assets.length - 1);
    const verticalOffset = 50;

    // Draw connections and water flow
    assets.slice(0, -1).forEach((_, i) => {
      const startX = i * horizontalGap + nodeWidth;
      const startY = height / 2 + (i % 2 === 0 ? -verticalOffset : verticalOffset);
      const endX = (i + 1) * horizontalGap;
      const endY = height / 2 + ((i + 1) % 2 === 0 ? -verticalOffset : verticalOffset);

      // Create curved path for water flow
      const flowPath = d3.path();
      flowPath.moveTo(startX, startY);
      
      const controlPoint1X = startX + (endX - startX) / 3;
      const controlPoint1Y = startY;
      const controlPoint2X = endX - (endX - startX) / 3;
      const controlPoint2Y = endY;
      
      flowPath.bezierCurveTo(
        controlPoint1X, controlPoint1Y,
        controlPoint2X, controlPoint2Y,
        endX, endY
      );

      // Add water flow background with 3D effect
      g.append("path")
        .attr("d", flowPath.toString())
        .attr("stroke", "url(#water-gradient)")
        .attr("stroke-width", 20)
        .attr("fill", "none")
        .attr("filter", "url(#drop-shadow)")
        .attr("class", "water-flow");

      // Add water pattern overlay
      g.append("path")
        .attr("d", flowPath.toString())
        .attr("stroke", "url(#water-pattern)")
        .attr("stroke-width", 20)
        .attr("fill", "none")
        .attr("class", "water-pattern");

      // Add animated particles
      const particleGroup = g.append("g").attr("class", "particles");
      const numParticles = 5;

      for (let j = 0; j < numParticles; j++) {
        particleGroup.append("circle")
          .attr("r", 2)
          .attr("fill", "#bfdbfe")
          .attr("filter", "url(#drop-shadow)")
          .append("animateMotion")
          .attr("dur", "3s")
          .attr("begin", `${j * (3 / numParticles)}s`)
          .attr("repeatCount", "indefinite")
          .attr("path", flowPath.toString());
      }

      // Add flow metrics from historian data
      const flowMetric = getTagValue(`${assets[i].id.toUpperCase()}_FLOW_01`);
      if (flowMetric) {
        const metricX = (startX + endX) / 2;
        const metricY = (startY + endY) / 2 - 20;

        const metricGroup = g.append("g")
          .attr("transform", `translate(${metricX},${metricY})`);

        metricGroup.append("rect")
          .attr("x", -40)
          .attr("y", -15)
          .attr("width", 80)
          .attr("height", 30)
          .attr("rx", 15)
          .attr("fill", "white")
          .attr("stroke", "#60a5fa")
          .attr("stroke-width", 1.5)
          .attr("filter", "url(#drop-shadow)");

        metricGroup.append("text")
          .attr("text-anchor", "middle")
          .attr("dy", "0.35em")
          .attr("class", "text-sm font-medium text-blue-600")
          .text(`${flowMetric.value.toFixed(1)} ${flowMetric.unit}`);
      }
    });

    // Draw nodes with 3D effect
    assets.forEach((asset, i) => {
      const x = i * horizontalGap;
      const y = height / 2 + (i % 2 === 0 ? -verticalOffset : verticalOffset);

      const nodeGroup = g.append("g")
        .attr("transform", `translate(${x},${y - nodeHeight/2})`)
        .attr("class", "cursor-pointer transition-transform duration-200 hover:scale-105")
        .on("click", () => setSelectedAsset(asset === selectedAsset ? null : asset));

      // Create 3D container
      const statusColor = asset.status === 'operational' ? '#22c55e' :
                         asset.status === 'warning' ? '#eab308' : '#ef4444';

      // Add 3D shadow
      nodeGroup.append("rect")
        .attr("width", nodeWidth)
        .attr("height", nodeHeight)
        .attr("rx", 12)
        .attr("transform", "translate(4, 4)")
        .attr("fill", "#000")
        .attr("opacity", 0.2)
        .attr("filter", "url(#drop-shadow)");

      // Main node body with metallic effect
      nodeGroup.append("rect")
        .attr("width", nodeWidth)
        .attr("height", nodeHeight)
        .attr("rx", 12)
        .attr("fill", "url(#metallic-gradient)")
        .attr("stroke", statusColor)
        .attr("stroke-width", 2)
        .attr("filter", "url(#inner-shadow)");

      // Add water level indicator with 3D effect
      const primaryMetric = asset.metrics.find(m => m.name.toLowerCase().includes('level'));
      if (primaryMetric) {
        const levelHeight = (primaryMetric.value / 100) * (nodeHeight - 10);
        nodeGroup.append("rect")
          .attr("x", 5)
          .attr("y", nodeHeight - 5 - levelHeight)
          .attr("width", nodeWidth - 10)
          .attr("height", levelHeight)
          .attr("rx", 8)
          .attr("fill", "url(#water-gradient)")
          .attr("opacity", 0.3)
          .attr("filter", "url(#inner-shadow)");
      }

      // Asset name with 3D text effect
      nodeGroup.append("text")
        .attr("x", nodeWidth / 2)
        .attr("y", 25)
        .attr("text-anchor", "middle")
        .attr("class", "text-sm font-medium")
        .attr("fill", "#1e293b")
        .attr("filter", "url(#drop-shadow)")
        .text(asset.name);

      // Primary metric display with 3D effect
      const displayMetric = primaryMetric || asset.metrics[0];
      if (displayMetric) {
        nodeGroup.append("text")
          .attr("x", nodeWidth / 2)
          .attr("y", nodeHeight / 2 + 10)
          .attr("text-anchor", "middle")
          .attr("class", "text-lg font-bold")
          .attr("fill", "#1e293b")
          .attr("filter", "url(#drop-shadow)")
          .text(displayMetric.value.toFixed(1));

        nodeGroup.append("text")
          .attr("x", nodeWidth / 2)
          .attr("y", nodeHeight / 2 + 30)
          .attr("text-anchor", "middle")
          .attr("class", "text-xs")
          .attr("fill", "#475569")
          .text(displayMetric.unit);
      }

      // Status indicator with glow effect
      nodeGroup.append("circle")
        .attr("cx", nodeWidth - 15)
        .attr("cy", 15)
        .attr("r", 6)
        .attr("fill", statusColor)
        .attr("filter", "url(#drop-shadow)");
    });

    // Add CSS animations
    const style = document.createElement('style');
    style.textContent = `
      .water-flow {
        animation: flowAnimation 3s linear infinite;
      }
      .water-pattern {
        animation: patternAnimation 3s linear infinite;
      }
      @keyframes flowAnimation {
        0% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -20; }
      }
      @keyframes patternAnimation {
        0% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -20; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [assets, selectedAsset, getTagValue]);

  if (assets.length < 2) {
    return (
      <div className="h-[600px] bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Process Flow Diagram</h3>
        <div className="h-[540px] flex items-center justify-center text-gray-500">
          Insufficient process data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Process Flow Diagram</h3>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm text-gray-600">Operational</span>
          </div>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-gray-600">Warning</span>
          </div>
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4 text-red-500" />
            <span className="text-sm text-gray-600">Error</span>
          </div>
        </div>
      </div>
      
      <div className="relative">
        <div className="h-[600px] overflow-hidden rounded-lg">
          <svg
            ref={svgRef}
            className="w-full h-full"
          />
        </div>

        {selectedAsset && (
          <div className="absolute bottom-4 left-4 right-4 p-6 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{selectedAsset.name}</h4>
                <p className="text-sm text-gray-600 mb-1">{selectedAsset.type}</p>
                <p className="text-sm text-gray-600 mb-1">Manufacturer: {selectedAsset.manufacturer}</p>
                <p className="text-sm text-gray-600">Model: {selectedAsset.model}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Status Information</h4>
                <p className="text-sm text-gray-600 mb-1">Status: {selectedAsset.status}</p>
                <p className="text-sm text-gray-600 mb-1">Installed: {selectedAsset.installationDate}</p>
                <p className="text-sm text-gray-600">Maintenance: {selectedAsset.maintenanceSchedule}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Current Metrics</h4>
                <div className="space-y-2">
                  {selectedAsset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between">
                      <span className="text-sm text-gray-600">{metric.name}:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};